/**
 * WeaveIn.ai Platform - Workflow Template List Component
 * Displays workflow templates with CRUD operations
 */

import React, { useState, useEffect } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Copy,
  Wrench,
  Bot,
  Building2,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  LayoutGrid,
  List,
  Play,
  Zap,
  Clock,
  GitBranch,
  MapPin,
  Rocket,
} from 'lucide-react';
import useWorkflowTemplates from '@/hooks/useWorkflows';
import usePrograms from '@/hooks/usePrograms';
import {
  WorkflowTemplate,
  WorkflowTemplateCreate,
  WorkflowTriggerType,
  WorkflowStepGaps,
  workflowsAPI,
} from '@/services/api/workflows';
import { WorkflowStepBuilder } from './WorkflowStepBuilder';
import { OnboardingTemplatesPanel } from './OnboardingTemplatesPanel';
import { organizationsAPI, type Organization, type UserOrganization } from '@/services/api/organizations';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { config } from '@/src/config';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';

// Employer Agentic Workflow Template Interface
interface EmployerWorkflowTemplate {
  id: number;
  employer_id: number;
  employer_name?: string;
  template_name: string;
  template_type: string;
  description?: string;
  trigger_type: string;
  trigger_config?: Record<string, unknown>;
  default_priority: string;
  max_attempts: number;
  escalation_threshold_days: number;
  communication_channel: string;
  message_template?: string;
  auto_create_referral: boolean;
  referral_program_id?: number;
  referral_priority?: string;
  ai_model_config?: {
    primary_model: string;
    decision_model: string;
    communication_model: string;
    model_parameters?: Record<string, unknown>;
  };
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface WorkflowTemplateListProps {
  onNavigate?: (tab: string, templateId?: number) => void;
  pageSize?: number;
}

export const WorkflowTemplateList: React.FC<WorkflowTemplateListProps> = ({
  onNavigate,
  pageSize = 50,
}) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [viewMode, setViewMode] = useState<'flow' | 'list' | 'onboarding'>('flow');

  const [selectedProgram, setSelectedProgram] = useState<number | undefined>();
  const [selectedActive, setSelectedActive] = useState<boolean | undefined>();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WorkflowTemplate | null>(null);
  const [buildingStepsFor, setBuildingStepsFor] = useState<{ id: number; name: string } | null>(null);
  // How many user-facing steps still do not say where they happen.
  //
  // Beside the templates rather than in a report nobody opens: a counter next
  // to the thing you are already editing gets fixed; a report does not. Left
  // out entirely when nothing is unmapped, because a permanent "0 unmapped"
  // badge is noise that trains people to stop reading the row.
  const [gaps, setGaps] = useState<WorkflowStepGaps | null>(null);
  useEffect(() => {
    let cancelled = false;
    workflowsAPI
      .getStepGaps()
      .then((g) => {
        if (!cancelled) setGaps(g);
      })
      // Silent: it is a hint about completeness, and failing to load it must
      // not put an error banner over a page whose actual job still works.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [buildingStepsFor]);

  // Clone functionality state
  const [userOrgs, setUserOrgs] = useState<UserOrganization[]>([]);
  const [allOrganizations, setAllOrganizations] = useState<Organization[]>([]);
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [cloningTemplate, setCloningTemplate] = useState<WorkflowTemplate | null>(null);
  const [targetOrgId, setTargetOrgId] = useState<number | ''>('');

  // Employer Agentic Workflow Templates state
  const [employerWorkflowTemplates, setEmployerWorkflowTemplates] = useState<EmployerWorkflowTemplate[]>([]);
  const [loadingEmployerTemplates, setLoadingEmployerTemplates] = useState(false);
  const [employerTemplatesExpanded, setEmployerTemplatesExpanded] = useState(true);
  const [employerTemplatesError, setEmployerTemplatesError] = useState<string | null>(null);
  const [isEmployerOrg, setIsEmployerOrg] = useState(false);

  const {
    templates: templatesData,
    total,
    page,
    loading,
    error,
    setPage,
    setFilters,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  } = useWorkflowTemplates({
    page_size: pageSize,
    program_id: selectedProgram,
    is_active: selectedActive,
  });

  const templates = templatesData ?? [];

  const programsHook = usePrograms();
  const programs = programsHook?.programs ?? [];

  // Check if user can clone templates (super_admin or ops_admin)
  const canCloneTemplates = userOrgs.some(org =>
    org.role === 'super_admin' || org.role === 'ops_admin'
  );

  // Fetch user organizations on mount and check if current org is employer
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const [myOrgs, orgs] = await Promise.all([
          organizationsAPI.getMyOrganizations(),
          organizationsAPI.listOrganizations(),
        ]);
        setUserOrgs(myOrgs);
        setAllOrganizations(orgs);

        // Check if current organization is an employer type
        const activeCboId = localStorage.getItem('active_cbo_id');
        if (activeCboId) {
          const currentOrg = orgs.find(org => org.id === parseInt(activeCboId));
          // Check if sponsor_type is EMPLOYER
          setIsEmployerOrg(currentOrg?.sponsor_type === 'EMPLOYER');
        }
      } catch (err) {
        console.error('Error loading organizations:', err);
      }
    };
    loadOrganizations();
  }, []);

  // Fetch employer agentic workflow templates (only for employer organizations)
  useEffect(() => {
    // Only load if organization is an employer type
    if (!isEmployerOrg) {
      setEmployerWorkflowTemplates([]);
      return;
    }

    const loadEmployerWorkflowTemplates = async () => {
      setLoadingEmployerTemplates(true);
      setEmployerTemplatesError(null);
      try {
        const token = localStorage.getItem('auth_token');
        const cboId = localStorage.getItem('active_cbo_id');

        // Fetch templates from all employers - using aggregated endpoint
        const response = await fetch(`${config.weaveinApi.baseUrl}/api/employer-workflows/all-templates`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId || '',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setEmployerWorkflowTemplates(data.templates || data || []);
        } else if (response.status === 404) {
          // Endpoint may not exist yet, try fetching from known employers
          console.warn('All-templates endpoint not found, skipping employer templates');
          setEmployerWorkflowTemplates([]);
        } else {
          throw new Error(`Failed to fetch employer templates: ${response.status}`);
        }
      } catch (err) {
        console.error('Error loading employer workflow templates:', err);
        setEmployerTemplatesError('Unable to load agentic workflow templates');
        setEmployerWorkflowTemplates([]);
      } finally {
        setLoadingEmployerTemplates(false);
      }
    };

    loadEmployerWorkflowTemplates();
  }, [isEmployerOrg]);

  const [formData, setFormData] = useState<Partial<WorkflowTemplateCreate> & { target_entity_type?: string }>({
    name: '',
    description: '',
    trigger_type: 'manual',
    is_active: true,
    target_entity_type: 'member',
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      trigger_type: 'manual',
      is_active: true,
      target_entity_type: 'member',
    });
    setShowCreateForm(false);
    setShowEditForm(false);
    setEditingTemplate(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await createTemplate(formData as WorkflowTemplateCreate);
    if (success) {
      alert('Workflow template created successfully');
      resetForm();
    }
  };

  const handleEdit = (template: WorkflowTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      trigger_type: template.trigger_type,
      trigger_config: template.trigger_config,
      is_active: template.is_active,
      program_id: template.program_id,
    });
    setShowEditForm(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTemplate) return;

    const success = await updateTemplate(editingTemplate.id, formData);
    if (success) {
      alert('Workflow template updated successfully');
      resetForm();
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (window.confirm(`Are you sure you want to delete workflow template "${name}"?`)) {
      const success = await deleteTemplate(id);
      if (success) {
        alert('Workflow template deleted successfully');
      }
    }
  };

  const [propagating, setPropagating] = useState<number | null>(null);

  const handlePropagate = async (templateId: number, templateName: string) => {
    if (!window.confirm(
      `Propagate changes from "${templateName}" to all active workflow instances?\n\n` +
      `This will update step names, types, and create document requirements for existing enrollments.`
    )) return;

    setPropagating(templateId);
    try {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      const res = await fetch(`${config.weaveinApi.baseUrl}/api/workflows/templates/${templateId}/propagate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': cboId || '',
          'Content-Type': 'application/json',
        },
      });

      if (res.ok) {
        const data = await res.json();
        alert(
          `Changes propagated successfully!\n\n` +
          `• ${data.active_instances} active instance(s) updated\n` +
          `• ${data.steps_created || 0} new step(s) created\n` +
          `• ${data.steps_synced || 0} step(s) synced\n` +
          `• ${data.doc_requirements_created} document requirement(s) created`
        );
      } else {
        const err = await res.json().catch(() => ({ detail: 'Unknown error' }));
        alert(`Failed to propagate: ${err.detail}`);
      }
    } catch (e: any) {
      alert(`Error: ${e.message}`);
    } finally {
      setPropagating(null);
    }
  };

  const handleFilterChange = (programId?: number, isActive?: boolean) => {
    setSelectedProgram(programId);
    setSelectedActive(isActive);
    setFilters({ program_id: programId, is_active: isActive });
  };

  const handleCloneClick = (template: WorkflowTemplate) => {
    setCloningTemplate(template);
    setTargetOrgId('');
    setShowCloneDialog(true);
  };

  const handleClone = async () => {
    if (!cloningTemplate || !targetOrgId) return;

    try {
      // Create a copy of the template for the target organization
      const clonedData: WorkflowTemplateCreate = {
        name: `${cloningTemplate.name} (Copy)`,
        description: cloningTemplate.description,
        trigger_type: cloningTemplate.trigger_type,
        trigger_config: cloningTemplate.trigger_config,
        is_active: true,
      };

      // Note: The backend should handle setting the cbo_id based on the target org
      // For now, we create it and the backend uses the X-CBO-ID header
      const originalCboId = localStorage.getItem('active_cbo_id');
      localStorage.setItem('active_cbo_id', String(targetOrgId));

      const success = await createTemplate(clonedData);

      // Restore original CBO ID
      if (originalCboId) {
        localStorage.setItem('active_cbo_id', originalCboId);
      }

      if (success) {
        alert(`Template "${cloningTemplate.name}" cloned successfully to the selected organization.`);
        setShowCloneDialog(false);
        setCloningTemplate(null);
        setTargetOrgId('');
      }
    } catch (err) {
      console.error('Error cloning template:', err);
      alert('Failed to clone template. Please try again.');
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  // If building steps, show the step builder
  if (buildingStepsFor) {
    return (
      <WorkflowStepBuilder
        templateId={buildingStepsFor.id}
        templateName={buildingStepsFor.name}
        onClose={() => setBuildingStepsFor(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5">
            <button
              onClick={() => setViewMode('flow')}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                viewMode === 'flow'
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
              Flow
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
              }`}
            >
              <List className="w-4 h-4" />
              List
            </button>
            <button
              onClick={() => setViewMode('onboarding')}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                viewMode === 'onboarding'
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
              }`}
            >
              <Rocket className="w-4 h-4" />
              {t('workflows.onboarding.tab', 'Onboarding')}
            </button>
          </div>

          {gaps && gaps.unmapped > 0 && (
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
              title={t(
                'workflows.gaps.tooltip',
                'A step with no page cannot be followed by somebody new, and the assistant cannot point anyone at it.'
              )}
            >
              <MapPin className="w-3.5 h-3.5" />
              {t('workflows.gaps.label', '{{unmapped}} of {{total}} steps have no page', {
                unmapped: gaps.unmapped,
                total: gaps.total,
              })}
            </span>
          )}
        </div>
        <Button
          variant="primary"
          icon={<Plus className="w-4 h-4" />}
          onClick={() => setShowCreateForm(true)}
        >
          {t('workflows.create_template', 'Create Template')}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>
            <strong>{t('workflows.error', 'Error:')}</strong> {error}
          </AlertDescription>
        </Alert>
      )}

      {/* Create Form Modal */}
      <Dialog open={showCreateForm} onOpenChange={(open) => !open && resetForm()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('workflows.create_workflow_template', 'Create Workflow Template')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="template-name" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('workflows.template_name_1', 'Template Name')}
              </Label>
              <Input
                id="template-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., ECM Enrollment Onboarding"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('workflows.description', 'Description')}
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('workflows.describe_what_this_workflow_does', 'Describe what this workflow does...')}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="trigger-type" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('workflows.trigger_type', 'Trigger Type')}
              </Label>
              <Select
                value={formData.trigger_type}
                onValueChange={(value) => setFormData({ ...formData, trigger_type: value as WorkflowTriggerType })}
              >
                <SelectTrigger id="trigger-type">
                  <SelectValue placeholder={t('workflows.trigger_type_3', 'Trigger Type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">{t('workflows.manual', 'Manual')}</SelectItem>
                  <SelectItem value="enrollment_created">{t('workflows.enrollment_created', 'Enrollment Created')}</SelectItem>
                  <SelectItem value="enrollment_status_changed">{t('workflows.enrollment_status_changed', 'Enrollment Status Changed')}</SelectItem>
                  <SelectItem value="contact_created">Contact Created</SelectItem>
                  <SelectItem value="contact_status_changed">Contact Status Changed</SelectItem>
                  <SelectItem value="activity_completed">{t('workflows.activity_completed', 'Activity Completed')}</SelectItem>
                  <SelectItem value="time_based">{t('workflows.timebased', 'Time-Based')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="program-select" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('workflows.program_optional', 'Program (Optional)')}
              </Label>
              <Select
                value={formData.program_id ? String(formData.program_id) : ''}
                onValueChange={(value) =>
                  setFormData({ ...formData, program_id: value ? parseInt(value) : undefined })
                }
              >
                <SelectTrigger id="program-select">
                  <SelectValue placeholder={t('workflows.all_programs', 'All Programs')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">{t('workflows.all_programs', 'All Programs')}</SelectItem>
                  {programs.map((program) => (
                    <SelectItem key={program.id} value={String(program.id)}>
                      {program.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is-active-create"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked as boolean })}
              />
              <Label htmlFor="is-active-create" className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-pointer">
                {t('workflows.active_1', 'Active')}
              </Label>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Target Entity Type
              </Label>
              <p className="text-xs text-gray-500">Who should this workflow be used for?</p>
              <div className="flex gap-2">
                {[
                  { value: 'member', label: 'Members Only' },
                  { value: 'contact', label: 'Contacts Only' },
                  { value: 'both', label: 'Members & Contacts' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, target_entity_type: opt.value })}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-all ${
                      formData.target_entity_type === opt.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 font-medium'
                        : 'border-gray-200 dark:border-gray-700 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={resetForm}>
                {t('workflows.cancel', 'Cancel')}
              </Button>
              <Button type="submit" variant="primary" loading={loading}>
                {t('workflows.create_template_1', 'Create Template')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Form Modal */}
      <Dialog open={showEditForm && !!editingTemplate} onOpenChange={(open) => !open && resetForm()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('workflows.edit_workflow_template', 'Edit Workflow Template')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="edit-template-name" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('workflows.template_name_2', 'Template Name')}
              </Label>
              <Input
                id="edit-template-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('workflows.description_1', 'Description')}
              </Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-trigger-type" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('workflows.trigger_type_1', 'Trigger Type')}
              </Label>
              <Select
                value={formData.trigger_type}
                onValueChange={(value) => setFormData({ ...formData, trigger_type: value as WorkflowTriggerType })}
              >
                <SelectTrigger id="edit-trigger-type">
                  <SelectValue placeholder={t('workflows.trigger_type_4', 'Trigger Type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">{t('workflows.manual_1', 'Manual')}</SelectItem>
                  <SelectItem value="enrollment_created">{t('workflows.enrollment_created_1', 'Enrollment Created')}</SelectItem>
                  <SelectItem value="enrollment_status_changed">{t('workflows.enrollment_status_changed_1', 'Enrollment Status Changed')}</SelectItem>
                  <SelectItem value="activity_completed">{t('workflows.activity_completed_1', 'Activity Completed')}</SelectItem>
                  <SelectItem value="time_based">{t('workflows.timebased_1', 'Time-Based')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-program-select" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('workflows.program_optional_1', 'Program (Optional)')}
              </Label>
              <Select
                value={formData.program_id ? String(formData.program_id) : ''}
                onValueChange={(value) =>
                  setFormData({ ...formData, program_id: value ? parseInt(value) : undefined })
                }
              >
                <SelectTrigger id="edit-program-select">
                  <SelectValue placeholder={t('workflows.all_programs_1', 'All Programs')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">{t('workflows.all_programs_1', 'All Programs')}</SelectItem>
                  {programs.map((program) => (
                    <SelectItem key={program.id} value={String(program.id)}>
                      {program.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is-active-edit"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked as boolean })}
              />
              <Label htmlFor="is-active-edit" className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-pointer">
                {t('workflows.active_2', 'Active')}
              </Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={resetForm}>
                {t('workflows.cancel_1', 'Cancel')}
              </Button>
              <Button type="submit" variant="primary" loading={loading}>
                {t('workflows.update_template', 'Update Template')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Filters — for the template list only. Program and status say nothing
          about onboarding plans, and leaving them on that tab implies they do. */}
      <div className={`flex gap-3 items-center ${viewMode === 'onboarding' ? 'hidden' : ''}`}>
        <Select
          value={selectedProgram ? String(selectedProgram) : ''}
          onValueChange={(value) => handleFilterChange(value ? parseInt(value) : undefined, selectedActive)}
        >
          <SelectTrigger id="filter-program" className="w-[200px]">
            <SelectValue placeholder={t('workflows.all_programs_2', 'All Programs')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">{t('workflows.all_programs_2', 'All Programs')}</SelectItem>
            {programs.map((program) => (
              <SelectItem key={program.id} value={String(program.id)}>
                {program.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={selectedActive === undefined ? 'all' : selectedActive ? 'active' : 'inactive'}
          onValueChange={(value) =>
            handleFilterChange(
              selectedProgram,
              value === 'all' ? undefined : value === 'active'
            )
          }
        >
          <SelectTrigger id="filter-status" className="w-[140px]">
            <SelectValue placeholder={t('workflows.status_2', 'Status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('workflows.all', 'All')}</SelectItem>
            <SelectItem value="active">{t('workflows.active', 'Active')}</SelectItem>
            <SelectItem value="inactive">{t('workflows.inactive', 'Inactive')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {viewMode === 'onboarding' ? (
        <OnboardingTemplatesPanel
          onOpenTemplate={(id, name) => setBuildingStepsFor({ id, name })}
        />
      ) : loading && templates.length === 0 ? (
        <LoadingSpinner size="lg" text={t('workflows.loading_workflow_templates', 'Loading workflow templates...')} />
      ) : templates.length === 0 ? (
        <div className="flex flex-col items-center py-16 text-center">
          <GitBranch className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
            {t('workflows.no_workflow_templates_found', 'No workflow templates found')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 max-w-sm">
            {selectedProgram || selectedActive !== undefined
              ? 'No templates match the current filters.'
              : 'Create your first workflow template to automate processes.'}
          </p>
          <Button
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => setShowCreateForm(true)}
          >
            {t('workflows.create_first_template', 'Create First Template')}
          </Button>
        </div>
      ) : viewMode === 'flow' ? (
        /* ── Flow view: card grid ── */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => {
            const triggerIcon = template.trigger_type === 'manual'
              ? <Play className="w-4 h-4" />
              : template.trigger_type === 'time_based'
              ? <Clock className="w-4 h-4" />
              : <Zap className="w-4 h-4" />;

            return (
              <div
                key={template.id}
                className={`group relative rounded-xl border bg-white dark:bg-gray-900 p-5 transition-shadow hover:shadow-md cursor-pointer ${
                  template.is_active
                    ? 'border-gray-200 dark:border-gray-700'
                    : 'border-gray-200 dark:border-gray-700 opacity-60'
                }`}
                onClick={() => setBuildingStepsFor(template)}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500">
                    {triggerIcon}
                    <span className="text-xs font-medium uppercase tracking-wide">
                      {template.trigger_type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <Badge variant={template.is_active ? 'success' : 'default'} size="sm">
                    {template.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>

                {/* Title */}
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1 line-clamp-1">
                  {template.name}
                </h3>
                {template.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
                    {template.description}
                  </p>
                )}

                {/* Meta */}
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>{template.program_name || 'All Programs'}</span>
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                  <span>{fmt.short(template.created_ts)}</span>
                </div>

                {/* Hover actions */}
                <div className="absolute top-3 right-3 hidden group-hover:flex gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleEdit(template); }}
                    className="p-1.5 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-gray-900 dark:hover:text-gray-100 shadow-sm"
                    title="Edit"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(template.id, template.name); }}
                    className="p-1.5 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-red-600 shadow-sm"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── List view: table ── */
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('workflows.template_name', 'Template Name')}</TableHead>
                <TableHead>{t('workflows.trigger_type_2', 'Trigger Type')}</TableHead>
                <TableHead>{t('workflows.program', 'Program')}</TableHead>
                <TableHead>{t('workflows.status_1', 'Status')}</TableHead>
                <TableHead>{t('workflows.created', 'Created')}</TableHead>
                <TableHead>{t('workflows.actions', 'Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((template) => (
                <TableRow
                  key={template.id}
                  className={template.is_active ? '' : 'opacity-60'}
                >
                  <TableCell>
                    <div className="font-semibold text-gray-900 dark:text-gray-100">
                      {template.name}
                    </div>
                    {template.description && (
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {template.description}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge variant="default" size="sm">
                        {template.trigger_type.replace(/_/g, ' ')}
                      </Badge>
                      {(template as any).target_entity_type && (template as any).target_entity_type !== 'member' && (
                        <Badge variant={(template as any).target_entity_type === 'contact' ? 'secondary' : 'outline'} size="sm">
                          {(template as any).target_entity_type === 'contact' ? 'Contacts' : 'Both'}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-900 dark:text-gray-100">
                    {template.program_name || 'All Programs'}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={template.is_active ? 'success' : 'default'}
                      size="sm"
                    >
                      {template.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-gray-900 dark:text-gray-100">
                    {fmt.short(template.created_ts)}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="primary"
                        icon={<Wrench className="w-4 h-4" />}
                        onClick={() => setBuildingStepsFor(template)}
                        title={t('workflows.design_workflow_steps', 'Design workflow steps')}
                      >
                        {t('workflows.design_steps', 'Design Steps')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        icon={<Pencil className="w-4 h-4" />}
                        onClick={() => handleEdit(template)}
                      >
                        {t('workflows.edit', 'Edit')}
                      </Button>
                      {canCloneTemplates && (
                        <Button
                          size="sm"
                          variant="outline"
                          icon={<Copy className="w-4 h-4" />}
                          onClick={() => handleCloneClick(template)}
                          title={t('workflows.clone_to_another_organization', 'Clone to another organization')}
                        >
                          {t('workflows.clone', 'Clone')}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="danger"
                        icon={<Trash2 className="w-4 h-4" />}
                        onClick={() => handleDelete(template.id, template.name)}
                      >
                        {t('workflows.delete', 'Delete')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        icon={<RefreshCw className={`w-4 h-4 ${propagating === template.id ? 'animate-spin' : ''}`} />}
                        onClick={() => handlePropagate(template.id, template.name)}
                        disabled={propagating === template.id}
                        title="Propagate template changes to all active workflow instances"
                      >
                        {propagating === template.id ? 'Propagating...' : 'Propagate'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Pagination */}
      {viewMode !== 'onboarding' && templates.length > 0 && (
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total} templates
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(1)} disabled={page === 1}>First</Button>
            <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 1}>Previous</Button>
            <span className="flex items-center px-3 text-sm text-gray-600 dark:text-gray-300">
              Page {page} of {totalPages}
            </span>
            <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page === totalPages}>Next</Button>
            <Button variant="outline" size="sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>Last</Button>
          </div>
        </div>
      )}

      {/* Agentic Workflow Templates Section - Only shown for employer organizations */}
      {isEmployerOrg && (
      <div className="mt-8">
        <Collapsible open={employerTemplatesExpanded} onOpenChange={setEmployerTemplatesExpanded}>
          <Card>
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                    <Bot className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="text-left">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {t('workflows.agentic_workflows', 'Agentic Workflow Templates')}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('workflows.agentic_workflows_desc', 'AI-powered automated workflows for wellness outreach')}
                    </p>
                  </div>
                  {employerWorkflowTemplates.length > 0 && (
                    <Badge variant="secondary" size="sm" className="ml-2">
                      {employerWorkflowTemplates.length}
                    </Badge>
                  )}
                </div>
                {employerTemplatesExpanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </div>
            </CollapsibleTrigger>

            <CollapsibleContent>
              {loadingEmployerTemplates ? (
                <div className="p-6 border-t">
                  <LoadingSpinner size="md" text={t('workflows.loading_agentic_templates', 'Loading agentic workflow templates...')} />
                </div>
              ) : employerTemplatesError ? (
                <div className="p-6 border-t">
                  <Alert variant="destructive">
                    <AlertDescription>{employerTemplatesError}</AlertDescription>
                  </Alert>
                </div>
              ) : employerWorkflowTemplates.length === 0 ? (
                <div className="p-6 border-t text-center">
                  <Bot className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    {t('workflows.no_agentic_templates', 'No Agentic Workflow Templates')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('workflows.no_agentic_templates_desc', 'Agentic workflow templates are configured in the Workflow Orchestration section.')}
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('workflows.template_name', 'Template Name')}</TableHead>
                      <TableHead>{t('workflows.employer', 'Employer')}</TableHead>
                      <TableHead>{t('workflows.type', 'Type')}</TableHead>
                      <TableHead>{t('workflows.ai_model', 'AI Model')}</TableHead>
                      <TableHead>{t('workflows.trigger', 'Trigger')}</TableHead>
                      <TableHead>{t('workflows.status_1', 'Status')}</TableHead>
                      <TableHead>{t('workflows.created', 'Created')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employerWorkflowTemplates.map((template) => (
                      <TableRow
                        key={`employer-${template.id}`}
                        className={template.is_active ? '' : 'opacity-60'}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Bot className="w-4 h-4 text-purple-500" />
                            <div>
                              <div className="font-semibold text-gray-900 dark:text-gray-100">
                                {template.template_name}
                              </div>
                              {template.description && (
                                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                  {template.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-900 dark:text-gray-100">
                              {template.employer_name || `Employer #${template.employer_id}`}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="default" size="sm">
                            {template.template_type.replace(/_/g, ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {template.ai_model_config ? (
                            <Badge variant="secondary" size="sm">
                              {template.ai_model_config.primary_model}
                            </Badge>
                          ) : (
                            <span className="text-gray-500 text-sm">Default</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" size="sm">
                            {template.trigger_type.replace(/_/g, ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={template.is_active ? 'success' : 'default'}
                            size="sm"
                          >
                            {template.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-900 dark:text-gray-100">
                          {fmt.short(template.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>
      )}

      {/* Clone Template Dialog */}
      <Dialog open={showCloneDialog} onOpenChange={setShowCloneDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('workflows.clone_workflow_template', 'Clone Workflow Template')}</DialogTitle>
          </DialogHeader>
          {cloningTemplate && (
            <div className="space-y-4">
              <p className="text-sm text-gray-900 dark:text-gray-100">
                {t('workflows.clone_1', 'Clone')} <strong>"{cloningTemplate.name}"</strong> to another organization.
                This will create a copy of the template with all its settings.
              </p>
              <div className="space-y-2">
                <Label htmlFor="target-org" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('workflows.target_organization', 'Target Organization')}
                </Label>
                <Select
                  value={targetOrgId ? String(targetOrgId) : ''}
                  onValueChange={(value) => setTargetOrgId(value ? parseInt(value) : '')}
                >
                  <SelectTrigger id="target-org">
                    <SelectValue placeholder={t('workflows.target_organization_1', 'Target Organization')} />
                  </SelectTrigger>
                  <SelectContent>
                    {allOrganizations.map((org) => (
                      <SelectItem key={org.id} value={String(org.id)}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Alert>
                <AlertDescription>
                  {t('workflows.the_cloned_template_will_be', 'The cloned template will be created in the selected organization and can be modified independently.')}
                </AlertDescription>
              </Alert>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloneDialog(false)}>
              {t('workflows.cancel_2', 'Cancel')}
            </Button>
            <Button
              variant="outline"
              icon={<Copy className="w-4 h-4" />}
              onClick={handleClone}
              disabled={!targetOrgId}
            >
              {t('workflows.clone_template', 'Clone Template')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkflowTemplateList;
